// components/ui/AutocompleteInput.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ModernInput } from './ModernInput.tsx';
import { LoadingSpinner } from './LoadingSpinner.tsx';

interface AutocompleteOption {
  id?: number;
  [key: string]: any;
}

interface AutocompleteInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  onSelect: (option: AutocompleteOption) => void;
  options?: AutocompleteOption[];
  loading?: boolean;
  displayKey?: string;
  icon?: React.ReactNode;
}

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({ 
  label, 
  placeholder, 
  value = '', 
  onChange, 
  onSelect, 
  options = [], 
  loading = false,
  displayKey = 'name',
  icon 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSelect = (option: AutocompleteOption) => {
    setInputValue(option[displayKey]);
    setIsOpen(false);
    onSelect(option);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={inputRef}>
      <ModernInput
        label={label}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        icon={icon}
      />
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 flex items-center justify-center">
              <LoadingSpinner />
              <span className="mr-2 text-gray-500">جاري البحث...</span>
            </div>
          ) : options.length > 0 ? (
            options.map((option, index) => (
              <div
                key={option.id || index}
                className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                onClick={() => handleSelect(option)}
              >
                <div className="font-medium text-gray-900">{option[displayKey]}</div>
                {option.subtitle && (
                  <div className="text-sm text-gray-500">{option.subtitle}</div>
                )}
              </div>
            ))
          ) : inputValue.length > 0 ? (
            <div className="p-4 text-gray-500 text-center">
              لم يتم العثور على نتائج لـ "{inputValue}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};